
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

export const useAuthCallback = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(true);

  // Handle direct manual navigation to dashboard
  const handleManualRedirect = () => {
    navigate("/dashboard", { replace: true });
  };

  useEffect(() => {
    const handleAuth = async () => {
      try {
        // Check if we're in an OAuth callback
        const hash = window.location.hash;
        const query = window.location.search;
        
        console.log("Auth callback - hash:", hash, "query:", query, "path:", window.location.pathname, "full URL:", window.location.href);
        
        // Check for errors in URL parameters
        const urlParams = new URLSearchParams(query);
        const errorParam = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');
        const stateParam = urlParams.get('state');
        
        // Verify OAuth state for security
        const storedOauthState = localStorage.getItem('oauthState');
        
        // If we have a state parameter and it doesn't match the stored one
        if (stateParam && storedOauthState && stateParam !== storedOauthState) {
          console.error("État OAuth non valide");
          setError("Erreur de sécurité: état OAuth non valide");
          toast.error("Erreur de sécurité lors de l'authentification", {
            duration: 5000
          });
          setTimeout(() => navigate("/login", { replace: true }), 3000);
          return;
        }
        
        // Clean up stored OAuth state
        if (storedOauthState) {
          localStorage.removeItem('oauthState');
        }
        
        if (errorParam) {
          console.error("Erreur d'authentification OAuth:", errorParam, errorDescription);
          setError(errorDescription || errorParam);
          toast.error(errorDescription || "Erreur lors de l'authentification", {
            duration: 5000
          });
          setTimeout(() => navigate("/login", { replace: true }), 3000);
          return;
        }

        // If user is already authenticated
        if (user) {
          console.log("Utilisateur déjà authentifié, redirection vers dashboard...");
          const needsOrgSetup = localStorage.getItem("needsOrgSetup");
          if (needsOrgSetup === "true") {
            navigate("/organization-setup", { replace: true });
          } else {
            navigate("/dashboard", { replace: true });
          }
          return;
        }
        
        // Try explicitly to get the session
        console.log("Récupération explicite de la session Supabase...");
        const { data } = await supabase.auth.getSession();
        
        if (data?.session) {
          console.log("Session trouvée, redirection vers dashboard...");
          const needsOrgSetup = localStorage.getItem("needsOrgSetup");
          if (needsOrgSetup === "true") {
            navigate("/organization-setup", { replace: true });
          } else {
            navigate("/dashboard", { replace: true });
          }
        } else {
          // Try to parse the hash for tokens
          if (hash && hash.includes('access_token')) {
            console.log("Token trouvé dans l'URL, tentative de traitement...");
            
            // Try to get session from hash
            const hashParams = new URLSearchParams(hash.substring(1));
            const accessToken = hashParams.get('access_token');
            const refreshToken = hashParams.get('refresh_token');
            
            if (accessToken) {
              console.log("Tentative d'établir une session avec le token d'accès");
              
              try {
                const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
                  access_token: accessToken,
                  refresh_token: refreshToken || ''
                });
                
                if (sessionError) {
                  console.error("Erreur lors de l'établissement de la session:", sessionError);
                  throw sessionError;
                }
                
                if (sessionData?.user) {
                  console.log("Session établie avec succès:", sessionData.user);
                  const needsOrgSetup = localStorage.getItem("needsOrgSetup");
                  if (needsOrgSetup === "true") {
                    navigate("/organization-setup", { replace: true });
                  } else {
                    navigate("/dashboard", { replace: true });
                  }
                  return;
                }
              } catch (tokenError) {
                console.error("Erreur lors du traitement du token:", tokenError);
              }
            }
            
            // If we're still here, try to get the user
            const { data: authData, error: authError } = await supabase.auth.getUser();
            
            if (authError) {
              console.error("Erreur lors de la récupération de l'utilisateur:", authError);
              setError("Échec de l'authentification avec les tokens fournis.");
              navigate("/login", { replace: true });
              return;
            }
            
            if (authData?.user) {
              console.log("Utilisateur récupéré après le hash:", authData.user);
              const needsOrgSetup = localStorage.getItem("needsOrgSetup");
              if (needsOrgSetup === "true") {
                navigate("/organization-setup", { replace: true });
              } else {
                navigate("/dashboard", { replace: true });
              }
              return;
            }
          }
          
          // Try to explicitly exchange the code for a session
          try {
            console.log("Tentative d'échange de code contre session...");
            const { data: exchangeData, error: exchangeError } = await supabase.auth.exchangeCodeForSession(window.location.href);
            
            if (exchangeError) {
              console.error("Erreur lors de l'échange du code:", exchangeError);
            } else if (exchangeData?.user) {
              console.log("Échange de code réussi, utilisateur:", exchangeData.user);
              const needsOrgSetup = localStorage.getItem("needsOrgSetup");
              if (needsOrgSetup === "true") {
                navigate("/organization-setup", { replace: true });
              } else {
                navigate("/dashboard", { replace: true });
              }
              return;
            }
          } catch (exchangeError) {
            console.error("Exception lors de l'échange du code:", exchangeError);
          }
          
          console.log("Pas de session trouvée, redirection vers login...");
          navigate("/login", { replace: true });
        }
      } catch (err) {
        console.error("Erreur lors du traitement de l'authentification:", err);
        setError("Une erreur est survenue lors du traitement de l'authentification.");
        toast.error("Erreur d'authentification", { duration: 5000 });
        setTimeout(() => navigate("/login", { replace: true }), 3000);
      } finally {
        setProcessing(false);
      }
    };

    handleAuth();
  }, [user, navigate]);

  return { error, processing, handleManualRedirect };
};
